import { Link } from "react-router-dom";
import { Zap, CalendarClock, ArrowLeftRight, Star, Building2, Lock, CheckCircle2 } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const FEATURES = [
  {
    icon: Zap,
    title: "Instant Card Liquidity",
    desc: "Need money today? Convert your card's limit into usable liquidity — fast, secure and without loan paperwork.",
  },
  {
    icon: CalendarClock,
    title: "Automated Bill Payments",
    desc: "Bills paid on schedule, every cycle — bypassing the high-interest penalties of a missed due date.",
  },
  {
    icon: ArrowLeftRight,
    title: "Balance Transfer Facility",
    desc: "Move outstanding balances to lower-interest arrangements and breathe easier every month.",
  },
  {
    icon: Star,
    title: "Reward Point Optimization",
    desc: "We structure payments so your reward points and cashback actually accumulate, not evaporate.",
  },
  {
    icon: Building2,
    title: "Commercial Rent Payments",
    desc: "Pay business rent through your card's limit — free up working capital while staying on terms with landlords.",
  },
];

const STEPS = [
  { n: "01", title: "Share Securely", desc: "A brief, encrypted handover of the card and billing details." },
  { n: "02", title: "We Process", desc: "The swipe or payment is executed through secure, compliant channels." },
  { n: "03", title: "You're Covered", desc: "Bill cleared, liquidity in hand, confirmation in your inbox." },
];

const CC_SERVICES = ["Credit Card Bill Payment", "Card Liquidity / Swiping", "Balance Transfer", "Reward Point Optimization", "Commercial Rent Payment"];

export default function CreditCard() {
  return (
    <div data-testid="creditcard-page">
      <PageHero
        eyebrow="Instant Liquidity Desk"
        title={["Credit Card", "Bill Swiping."]}
        sub="Fast, secure credit card liquidity when cash is tight — and automated bill payments so you never see a high-interest penalty line again."
      >
        <ScrollCta href="#enquire" label="Enquire Now" testId="enquire-now-btn" />
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-navy" data-testid="creditcard-instant-badge">
          <Zap className="h-4 w-4 text-amber-600" /> Instant Processing
        </span>
      </PageHero>

      {/* FEATURES */}
      <section className="py-16 sm:py-24 lg:py-28" data-testid="creditcard-features-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">The Facility</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Liquidity, without the loan ceremony.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <div className="card-lift h-full rounded-3xl border border-slate-200 bg-white p-7" data-testid={`creditcard-feature-${i + 1}`}>
                  <f.icon className="h-8 w-8 text-amber-600" />
                  <h3 className="mt-6 font-display text-xl font-extrabold text-navy">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.desc}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="grain flex h-full flex-col justify-between rounded-3xl bg-navy p-7">
                <Lock className="h-8 w-8 text-gold" />
                <div>
                  <h3 className="mt-8 font-display text-xl font-extrabold text-white">Secure by design</h3>
                  <p className="mt-2 text-sm text-white/65">
                    Encrypted handovers, compliant processing channels and instant confirmations — every single time.
                  </p>
                  <Link to="/contact" className="mt-5 inline-block rounded-full bg-gold px-6 py-3 text-sm font-bold text-navy transition hover:-translate-y-0.5" data-testid="creditcard-security-cta">
                    Talk to the Desk
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-y border-slate-200 bg-[#F8FAFC] py-16 sm:py-20" data-testid="creditcard-steps-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">How it works</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Three steps. Same-day clarity.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-7">
                  <span className="font-display text-4xl font-black text-gold/40">{s.n}</span>
                  <h3 className="mt-4 font-display text-lg font-extrabold text-navy">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRE */}
      <section id="enquire" className="py-16 sm:py-24" data-testid="creditcard-enquire-section">
        <div className="container-x grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Enquire Now</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Tell us the bill. We'll handle the rest.
            </h2>
            <p className="mt-5 max-w-md text-slate-600">
              One quick enquiry and the liquidity desk calls you back with the exact plan — amount, charges and timing,
              before you commit to anything.
            </p>
            <ul className="mt-8 space-y-3">
              {["Instant processing on working hours", "Secure, confidential transactions", "Transparent charges, always up front"].map((pt) => (
                <li key={pt} className="flex items-start gap-3 text-sm font-bold text-navy">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                  {pt}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8" data-testid="creditcard-enquire-form-card">
              <LeadForm
                source="creditcard-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={CC_SERVICES}
                cta="Enquire Now"
                idPrefix="creditcard-enquire"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
